// src/pages/business/sales/RecordSalePage.tsx
// Web adaptation of mobile RecordSaleScreen - Point of Sale functionality

import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ShoppingCart, Plus, Trash2, Package, Search,
  CreditCard, Check, X, Loader2, AlertCircle, Receipt, Printer, Bluetooth, History, ChevronLeft, ChevronRight
} from 'lucide-react';
import apiClient from '@/services/apiClient';

interface InventoryItem {
  id: string;
  name: string;
  price: number;
  sku?: string;
  barcode?: string;
  stock_quantity?: number;
  category?: string;
}

interface CartItem extends InventoryItem {
  cartId: string;
}

export const RecordSalePage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [saleComplete, setSaleComplete] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [printerConnected, setPrinterConnected] = useState(false);
  const [connectingPrinter, setConnectingPrinter] = useState(false);
  const [cartCollapsed, setCartCollapsed] = useState(false);
  const [cartWidth, setCartWidth] = useState(480);
  const [showSalesHistory, setShowSalesHistory] = useState(false);
  const [recentSales, setRecentSales] = useState<any[]>([]);

  useEffect(() => {
    fetchInventory();
    fetchRecentSales();
  }, [businessId]);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/businesses/${businessId}/products`);
      setInventory(data?.products || data || []);
    } catch (err: any) {
      console.error('Failed to fetch inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentSales = async () => {
    try {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();
      const params = new URLSearchParams({ page: '1', limit: '10', from, to });
      const { data } = await apiClient.get(`/businesses/${businessId}/sales?${params.toString()}`);
      setRecentSales(data?.sales || []);
    } catch (err: any) {
      console.error('Failed to fetch recent sales:', err);
    }
  };

  const filteredInventory = useMemo(() => {
    if (!searchQuery) return inventory;
    const query = searchQuery.toLowerCase();
    return inventory.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query) ||
        item.barcode?.toLowerCase().includes(query)
    );
  }, [inventory, searchQuery]);

  const total = useMemo(() => {
    return cart.reduce((sum, item) => sum + (item.price || 0), 0);
  }, [cart]);

  const tax = useMemo(() => {
    return total * 0.16; // 16% VAT
  }, [total]);

  const handleAddToCart = (item: InventoryItem) => {
    const cartItem: CartItem = {
      ...item,
      cartId: `${item.id}-${Date.now()}-${Math.random()}`,
    };
    setCart([...cart, cartItem]);
    setShowItemSelector(false);
    setSearchQuery('');
  };

  const handleRemoveFromCart = (index: number) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  const handleCompleteSale = async () => {
    if (cart.length === 0) {
      alert('Please add at least one item to the cart.');
      return;
    }

    setProcessing(true);
    try {
      // Generate receipt number
      const receipt = `REC-${Date.now().toString().slice(-8)}`;
      setReceiptNumber(receipt);

      // In a real implementation, this would call the API to record the sale
      // await apiClient.post(`/businesses/${businessId}/sales`, {
      //   items: cart.map(item => ({
      //     product_id: item.id,
      //     quantity: 1,
      //     unit_price: item.price,
      //     total_price: item.price,
      //   })),
      //   total_amount: total,
      //   payment_method: 'cash',
      //   receipt_number: receipt,
      // });

      setSaleComplete(true);
      fetchRecentSales(); // Refresh sales after completing
    } catch (err: any) {
      console.error('Failed to complete sale:', err);
      alert('Failed to complete sale. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleNewSale = () => {
    setCart([]);
    setSaleComplete(false);
    setReceiptNumber('');
  };

  const handlePrintReceipt = () => {
    const printWindow = window.open('', '', 'width=300,height=600');
    if (!printWindow) {
      alert('Please allow popups to print receipts');
      return;
    }

    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${receiptNumber}</title>
        <style>
          @media print {
            @page { margin: 0; size: 80mm auto; }
          }
          body {
            font-family: 'Courier New', monospace;
            width: 80mm;
            margin: 0 auto;
            padding: 10mm;
            font-size: 12px;
          }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px dashed #000; padding-bottom: 10px; }
          .header h1 { margin: 0; font-size: 20px; }
          .header p { margin: 5px 0; }
          .items { margin: 20px 0; }
          .item { display: flex; justify-content: space-between; margin: 8px 0; }
          .totals { border-top: 2px dashed #000; padding-top: 10px; margin-top: 10px; }
          .total-row { display: flex; justify-content: space-between; margin: 5px 0; }
          .total-row.grand { font-size: 16px; font-weight: bold; margin-top: 10px; }
          .footer { text-align: center; margin-top: 20px; border-top: 2px dashed #000; padding-top: 10px; }
          .footer p { margin: 5px 0; font-size: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>MAJENGO POS</h1>
          <p>Receipt: ${receiptNumber}</p>
          <p>Date: ${new Date().toLocaleString()}</p>
        </div>
        
        <div class="items">
          ${cart.map((item, idx) => `
            <div class="item">
              <span>${idx + 1}. ${item.name}</span>
              <span>KES ${item.price.toLocaleString()}</span>
            </div>
          `).join('')}
        </div>
        
        <div class="totals">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>KES ${total.toLocaleString()}</span>
          </div>
          <div class="total-row">
            <span>VAT (16%):</span>
            <span>KES ${tax.toLocaleString()}</span>
          </div>
          <div class="total-row grand">
            <span>TOTAL:</span>
            <span>KES ${(total + tax).toLocaleString()}</span>
          </div>
        </div>
        
        <div class="footer">
          <p>Thank you for your business!</p>
          <p>Powered by Majengo</p>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
            setTimeout(() => window.close(), 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  };

  const handleConnectPrinter = async () => {
    setConnectingPrinter(true);
    try {
      // Check for Web Bluetooth API support
      if ('bluetooth' in navigator) {
        const device = await (navigator.bluetooth as any).requestDevice({
          filters: [{ services: ['000018f0-0000-1000-8000-00805f9b34fb'] }],
          optionalServices: ['battery_service']
        });
        setPrinterConnected(true);
        alert(`Connected to ${device.name}`);
      } else {
        alert('Bluetooth is not supported in this browser. Use Chrome/Edge on desktop or Android.');
      }
    } catch (err: any) {
      console.error('Failed to connect printer:', err);
      alert('Failed to connect to printer. Make sure Bluetooth is enabled.');
    } finally {
      setConnectingPrinter(false);
    }
  };

  if (saleComplete) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-light-card dark:bg-dark-card rounded-2xl p-8 text-center shadow-lg">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: 'rgba(39,174,96,0.12)' }}
          >
            <Check size={40} style={{ color: '#27ae60' }} />
          </div>

          <h2 className="text-2xl font-bold mb-2" style={{ color: '#0F172A' }}>
            Sale Complete!
          </h2>

          <div
            className="bg-light-bg dark:bg-dark-bg rounded-xl p-4 mb-6"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <Receipt size={20} style={{ color: '#64748B' }} />
              <span className="text-sm font-medium" style={{ color: '#64748B' }}>
                Receipt Number
              </span>
            </div>
            <p className="text-2xl font-bold" style={{ color: '#4F46E5' }}>
              {receiptNumber}
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: '#64748B' }}>Items Sold</span>
              <span className="font-semibold" style={{ color: '#0F172A' }}>{cart.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: '#64748B' }}>Total Amount</span>
              <span className="font-bold text-lg" style={{ color: '#4F46E5' }}>
                KES {total.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handlePrintReceipt}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-colors"
              style={{ backgroundColor: '#27ae60', color: '#fff' }}
            >
              <Printer size={20} />
              Print Receipt
            </button>
            
            <div className="flex gap-3">
              <button
                onClick={handleNewSale}
                className="flex-1 py-3 rounded-xl font-semibold transition-colors"
                style={{ backgroundColor: '#4F46E5', color: '#fff' }}
              >
                New Sale
              </button>
              <button
                onClick={() => navigate(`/business/${businessId}/products`)}
                className="flex-1 py-3 rounded-xl font-semibold border transition-colors"
                style={{ borderColor: '#4F46E5', color: '#4F46E5' }}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[100dvh] w-full overflow-hidden bg-light-bg dark:bg-dark-bg">
      {/* Header - Full Width */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/business/${businessId}/products`)}
              className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <ArrowLeft size={24} style={{ color: '#4F46E5' }} />
            </button>
            <div>
              <p className="font-bold text-lg" style={{ color: '#4F46E5' }}>Point of Sale</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSalesHistory(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors"
              style={{
                backgroundColor: 'rgba(79,70,229,0.12)',
                color: '#4F46E5'
              }}
            >
              <History size={18} />
              <span className="hidden sm:inline">Recent Sales</span>
            </button>
            
            <button
              onClick={handleConnectPrinter}
              disabled={connectingPrinter || printerConnected}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              style={{
                backgroundColor: printerConnected ? '#27ae60' : 'rgba(79,70,229,0.12)',
                color: printerConnected ? '#fff' : '#4F46E5'
              }}
            >
              {connectingPrinter ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Bluetooth size={18} />
              )}
              <span className="hidden sm:inline">
                {printerConnected ? 'Printer Connected' : 'Connect Printer'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area - Split Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Cart & Total */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className="p-4 space-y-4">
            {/* Total Display */}
            <div
              className="rounded-xl p-6 text-white"
              style={{ backgroundColor: '#4F46E5' }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm opacity-80">Subtotal</span>
                <span className="text-lg font-semibold">KES {total.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm opacity-80">Tax (VAT 16%)</span>
                <span className="text-lg font-semibold">KES {tax.toLocaleString()}</span>
              </div>
              <div className="border-t border-white/30 pt-3 mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold">TOTAL</span>
                  <span className="text-3xl font-bold">KES {total.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Cart Items */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold" style={{ color: '#0F172A' }}>Cart Items</h3>
                <span
                  className="px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: 'rgba(79,70,229,0.12)', color: '#4F46E5' }}
                >
                  {cart.length} items
                </span>
              </div>

              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <ShoppingCart size={48} style={{ color: '#94A3B8' }} />
                  <p className="font-medium" style={{ color: '#64748B' }}>
                    Your cart is empty
                  </p>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>
                    Add items to get started
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {cart.map((item, index) => (
                    <div
                      key={item.cartId}
                      className="flex items-center gap-4 p-3 bg-light-bg dark:bg-dark-bg rounded-xl"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate" style={{ color: '#0F172A' }}>
                          {item.name}
                        </p>
                        <p className="text-sm" style={{ color: '#64748B' }}>
                          {item.sku && `SKU: ${item.sku}`}
                        </p>
                      </div>
                      <p className="font-semibold" style={{ color: '#4F46E5' }}>
                        KES {item.price.toLocaleString()}
                      </p>
                      <button
                        onClick={() => handleRemoveFromCart(index)}
                        className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <Trash2 size={18} style={{ color: '#e74c3c' }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Resizable Divider */}
        <div 
          className="w-1 bg-light-border dark:bg-dark-border hover:bg-primary dark:hover:bg-golden cursor-col-resize transition-colors relative group"
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startWidth = cartWidth;
            
            const handleMouseMove = (moveEvent: MouseEvent) => {
              const diff = startX - moveEvent.clientX;
              const newWidth = Math.min(Math.max(startWidth + diff, 380), 600);
              setCartWidth(newWidth);
            };
            
            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };
            
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        >
          <div className="absolute inset-y-0 -left-1 -right-1" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-12 bg-primary dark:bg-golden rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>

        {/* Right: Products Sidebar */}
        <div 
          className="relative bg-light-card dark:bg-dark-card transition-all duration-300 flex flex-col overflow-hidden"
          style={{ 
            width: cartCollapsed ? '64px' : `${cartWidth}px`, 
            minWidth: cartCollapsed ? '64px' : '380px', 
            maxWidth: cartCollapsed ? '64px' : '600px'
          }}
        >
          {/* Collapse/Expand Button */}
          <button
            onClick={() => setCartCollapsed(!cartCollapsed)}
            className="absolute -left-3 top-4 z-10 p-2 rounded-full bg-primary text-white shadow-lg hover:bg-primary/90 transition-all"
            title={cartCollapsed ? 'Show Products' : 'Hide Products'}
          >
            {cartCollapsed ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>

          {cartCollapsed ? (
            /* Collapsed State */
            <div className="flex flex-col items-center h-full py-4 gap-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <Package size={20} className="text-primary" />
              </div>
              <div className="w-8 h-8 rounded-full bg-primary text-white font-bold text-xs flex items-center justify-center">
                {filteredInventory.length}
              </div>
            </div>
          ) : (
            /* Expanded State */
            <>
              {/* Sidebar Header */}
              <div className="p-4 border-b border-light-border dark:border-dark-border">
                <h3 className="font-bold text-lg" style={{ color: '#0F172A' }}>Available Products</h3>
                <p className="text-xs mt-1" style={{ color: '#64748B' }}>{filteredInventory.length} products</p>
              </div>

              {/* Search */}
              <div className="p-4 border-b border-light-border dark:border-dark-border">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                    style={{
                      borderColor: '#E2E8F0',
                      color: '#0F172A',
                      backgroundColor: 'transparent',
                    }}
                  />
                </div>
              </div>

              {/* Products List */}
              <div className="flex-1 overflow-y-auto p-4">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 size={32} className="animate-spin" style={{ color: '#4F46E5' }} />
                  </div>
                ) : filteredInventory.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <Package size={40} style={{ color: '#94A3B8' }} />
                    <p className="text-sm font-medium" style={{ color: '#64748B' }}>No products found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredInventory.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleAddToCart(item)}
                        disabled={item.stock_quantity === 0}
                        className="w-full text-left p-3 rounded-lg transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed border"
                        style={{
                          backgroundColor: 'rgba(79,70,229,0.04)',
                          borderColor: 'rgba(79,70,229,0.1)',
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate" style={{ color: '#0F172A' }}>
                              {item.name}
                            </p>
                            {(item.sku || item.barcode) && (
                              <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>
                                {item.sku || item.barcode}
                              </p>
                            )}
                            {item.stock_quantity !== undefined && (
                              <span
                                className={`inline-block text-xs px-2 py-0.5 rounded-full mt-1 ${
                                  item.stock_quantity > 0
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                }`}
                              >
                                {item.stock_quantity > 0 ? `${item.stock_quantity} in stock` : 'Out of stock'}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm" style={{ color: '#4F46E5' }}>
                              KES {item.price.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom Action Bar */}
      {cart.length > 0 && (
        <div className="bg-light-card dark:bg-dark-card border-t border-light-border dark:border-dark-border p-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowItemSelector(!showItemSelector)}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors"
              style={{ backgroundColor: '#d1a02f', color: '#fff' }}
            >
              <Plus size={20} />
              Add Item
            </button>
            <button
              onClick={handleCompleteSale}
              disabled={processing || cart.length === 0}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#27ae60' }}
            >
              {processing ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <>
                  <Check size={20} />
                  Complete Sale
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Recent Sales Modal */}
      {showSalesHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSalesHistory(false)} />
          <div className="relative w-full max-w-3xl bg-light-card dark:bg-dark-card rounded-2xl shadow-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-light-border dark:border-dark-border">
              <div>
                <h3 className="font-bold text-xl" style={{ color: '#0F172A' }}>Recent Sales (Today)</h3>
                <p className="text-sm mt-1" style={{ color: '#64748B' }}>{recentSales.length} sales</p>
              </div>
              <button 
                onClick={() => setShowSalesHistory(false)} 
                className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
              >
                <X size={24} style={{ color: '#64748B' }} />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[60vh] p-6">
              {recentSales.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Receipt size={48} style={{ color: '#94A3B8' }} />
                  <p className="font-medium" style={{ color: '#64748B' }}>No sales today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentSales.map((sale: any) => (
                    <div 
                      key={sale.id}
                      className="p-4 rounded-xl border border-light-border dark:border-dark-border hover:shadow-md transition-shadow"
                      style={{ backgroundColor: 'rgba(79,70,229,0.02)' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-lg" style={{ color: '#4F46E5' }}>
                          KES {Number(sale.total_amount || 0).toLocaleString()}
                        </span>
                        <span className="text-xs" style={{ color: '#64748B' }}>
                          {new Date(sale.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                      {sale.customer_name && (
                        <p className="text-sm" style={{ color: '#0F172A' }}>
                          Customer: {sale.customer_name}
                        </p>
                      )}
                      {sale.payment_method && (
                        <p className="text-xs mt-1" style={{ color: '#64748B' }}>
                          Payment: {sale.payment_method}
                        </p>
                      )}
                      {sale.sale_items && sale.sale_items.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {sale.sale_items.slice(0, 3).map((item: any, idx: number) => (
                            <span 
                              key={idx}
                              className="text-xs px-2 py-1 rounded-md" 
                              style={{ backgroundColor: 'rgba(79,70,229,0.1)', color: '#4F46E5' }}
                            >
                              {item.products?.name || 'Item'} ×{item.quantity}
                            </span>
                          ))}
                          {sale.sale_items.length > 3 && (
                            <span className="text-xs px-2 py-1" style={{ color: '#64748B' }}>
                              +{sale.sale_items.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-light-border dark:border-dark-border">
              <button
                onClick={() => {
                  setShowSalesHistory(false);
                  navigate(`/business/${businessId}/sales`);
                }}
                className="w-full py-3 rounded-xl font-semibold transition-colors"
                style={{ backgroundColor: '#4F46E5', color: '#fff' }}
              >
                View Full Sales History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Selector Modal */}
      {showItemSelector && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowItemSelector(false)} />
          <div className="relative w-full max-w-2xl bg-light-card dark:bg-dark-card rounded-t-2xl sm:rounded-2xl p-6 max-h-[70vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg" style={{ color: '#0F172A' }}>Select Product</h3>
              <button onClick={() => setShowItemSelector(false)} className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg">
                <X size={20} style={{ color: '#64748B' }} />
              </button>
            </div>

            <div className="relative mb-4">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: '#E2E8F0',
                  color: '#0F172A',
                  backgroundColor: 'transparent',
                }}
              />
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {filteredInventory.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleAddToCart(item)}
                  disabled={item.stock_quantity === 0}
                  className="w-full text-left p-3 rounded-xl transition-all hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium" style={{ color: '#0F172A' }}>{item.name}</p>
                    <p className="text-sm" style={{ color: '#64748B' }}>
                      KES {item.price.toLocaleString()}
                      {item.stock_quantity === 0 && ' - Out of stock'}
                    </p>
                  </div>
                  <Plus size={18} style={{ color: '#4F46E5' }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordSalePage;